import { RobocloudDTO } from '@robocorp/robocloud-common/lib/types';

import { secretService } from '../services/secretsAPI';
import { WithWorkspaceId } from '~/types/common';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/**
 * These array buffer conversion functions are from Cyrpto API official documentation examples
 * https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/importKey
 * https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
 */
const str2ab = (str: string): ArrayBuffer => {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0, strLen = str.length; i < strLen; i += 1) {
    bufView[i] = str.charCodeAt(i);
  }
  return buf;
};

const ab2str = (buffer: ArrayBuffer): string =>
  String.fromCharCode.apply(null, Array.from(new Uint8Array(buffer)));

/**
 * Decode Base64 to Hexadecimal string
 */
const base64ToHex = (value: string) => {
  return atob(value)
    .split('')
    .map((c) => c.charCodeAt(0).toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Generate AES key used to encrypt the secret value
 */
const generateAESKey = async (): Promise<CryptoKey> =>
  crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256,
    },
    true,
    ['encrypt', 'decrypt'],
  );

/**
 * Generates AES key and encrypts the secret value with it
 */
const encryptSecretValueWithAES = async (
  formattedValue: string | undefined,
): Promise<{
  value: string;
  iv: string;
  AESkey: CryptoKey;
  authTag: string;
}> => {
  const AESkey: CryptoKey = await generateAESKey();
  const generatedIv = crypto.getRandomValues(new Uint8Array(16));
  const encodedValue = encoder.encode(formattedValue);
  const encryptedValueBuf = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: generatedIv,
      tagLength: 128,
    },
    AESkey,
    encodedValue,
  );

  // WEB Crypto API attaches the auth tag at the end of the encrypted value but does not offer a function to export the GCM auth tag
  // We need to get the 128 bit tag from the end by slicing 16 bytes from the end of the encrypted value
  const authTagBuf = encryptedValueBuf.slice(encryptedValueBuf.byteLength - 16);

  // We will also trim the encrytped value to not contain the auth tag as backend does not support it
  const trimmedEncryptedValueBuf = encryptedValueBuf.slice(0, encryptedValueBuf.byteLength - 16);

  // Encode the array buffers to string
  const encryptedValueStr = ab2str(trimmedEncryptedValueBuf);
  const ivStr = ab2str(generatedIv.buffer);
  const authTagStr = ab2str(authTagBuf);

  // Encode the string to base64
  const value = btoa(encryptedValueStr);
  const iv = btoa(ivStr);
  const authTag = btoa(authTagStr);

  return {
    value,
    iv,
    AESkey,
    authTag,
  };
};

/**
 * Get the public RSA encryption key from the backend and import it as usable CryptoKey using Web Crypto API
 */
const getPublicEncryptionKey = async ({
  workspaceId,
}: WithWorkspaceId): Promise<CryptoKey | undefined> => {
  const publicKeyResponse = await secretService.getPublicKey({ workspaceId });

  if (!publicKeyResponse.ok || publicKeyResponse.data === undefined) return undefined;

  const decodedKey = atob(publicKeyResponse.data);
  const keyArrayBuffer: ArrayBuffer = str2ab(decodedKey);

  return crypto.subtle.importKey(
    'spki',
    keyArrayBuffer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt'],
  );
};

/**
 * Gets the public RSA key from backend and encrypts the AES key with it
 */
const encryptAESKeyWithRSA = async (
  { workspaceId }: WithWorkspaceId,
  AESKey: CryptoKey,
): Promise<string> => {
  // Export the AES key to a format that can be encrypted
  const exportedAESBuffer: ArrayBuffer = await crypto.subtle.exportKey('raw', AESKey);
  const encodedAES = new Uint8Array(exportedAESBuffer);

  const encryptionKey = await getPublicEncryptionKey({ workspaceId });
  if (!encryptionKey) throw new Error('Error getting public encryption key');

  const encryptedAESBuffer: ArrayBuffer = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    encryptionKey,
    encodedAES,
  );

  // Encode ArrayBuffer to string and the string to base64
  const AESKeyStr = ab2str(encryptedAESBuffer);
  const encryptedAES = btoa(AESKeyStr);

  return encryptedAES;
};

/**
 * Generate RSA key pair to encrypt the AES key
 */
export const generateRSAKeyPair = async (): Promise<CryptoKeyPair> =>
  crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 4096,
      publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'decrypt'],
  );

/**
 * Export public RSA CryptoKey as Base64 string
 */
export const exportRSAPublicKey = async (publicKey: CryptoKey): Promise<string> => {
  const publicKeyBuffer: ArrayBuffer = await crypto.subtle.exportKey('spki', publicKey);

  const publicKeyString = ab2str(publicKeyBuffer);
  const publicKeyBase64 = btoa(publicKeyString);
  return publicKeyBase64;
};

/**
 * Uses the AES key to decrypt the secret value
 */
const decryptSecretValueWithAES = async (
  encryptedSecret: RobocloudDTO.EncryptedSecretV2,
  AESKey: CryptoKey,
): Promise<string> => {
  const { encryption } = encryptedSecret;

  const iv = base64ToHex(encryption.iv);
  const encodedIv = encoder.encode(iv);

  const authTagString = atob(encryption.authTag);
  const encryptedValueString = atob(encryptedSecret.value);
  // Backend Node crypto API has encryption value and auth tag separately.
  // However WEB Crypto API needs them together so we need to concat the auth tag at the end
  const cipherText = encryptedValueString + authTagString;
  const encryptedValueBuffer: ArrayBuffer = str2ab(cipherText);
  const encodedValue = new Uint8Array(encryptedValueBuffer);

  const valueBuffer: ArrayBuffer = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: encodedIv,
    },
    AESKey,
    encodedValue,
  );

  const value = decoder.decode(valueBuffer);
  return value;
};

/**
 * Decrypts the AES Key with the RSA privateKey and imports the AES key as usable CryptoKey using Web Crypto API
 */
const decryptAESKeyWithRSA = async (
  encryptedAES: string,
  privateKey: CryptoKey,
): Promise<CryptoKey> => {
  const encryptedAESString = atob(encryptedAES);
  const encryptedAESBuffer: ArrayBuffer = str2ab(encryptedAESString);
  const encodedEncryptedAES = new Uint8Array(encryptedAESBuffer);
  const decryptedAESBuffer: ArrayBuffer = await crypto.subtle.decrypt(
    { name: 'RSA-OAEP' },
    privateKey,
    encodedEncryptedAES,
  );

  const encodedAES = new Uint8Array(decryptedAESBuffer);

  return crypto.subtle.importKey('raw', encodedAES, 'AES-GCM', true, ['encrypt', 'decrypt']);
};

/**
 * Encrypts the secret value for transit to the backend
 */
export const encrypt = async (
  { workspaceId }: WithWorkspaceId,
  formattedValue: string | undefined,
): Promise<Pick<RobocloudDTO.EncryptedSecretV2, 'value' | 'encryption'>> => {
  const encryptionScheme = RobocloudDTO.EncryptionSchemes.V2;
  const { value, iv, AESkey, authTag } = await encryptSecretValueWithAES(formattedValue);
  const encryptedAES = await encryptAESKeyWithRSA({ workspaceId }, AESkey);

  return {
    value,
    encryption: {
      encryptedAES,
      iv,
      encryptionScheme,
      authTag,
    },
  };
};

/**
 * Decrypts the encrypted secret value from the backend
 */
export const decrypt = async (
  encryptedSecret: RobocloudDTO.EncryptedSecretV2,
  privateKey: CryptoKey,
): Promise<RobocloudDTO.Secret> => {
  // Decrypt AES key by using the RSA key
  const { encryption } = encryptedSecret;
  const AESKey: CryptoKey = await decryptAESKeyWithRSA(encryption.encryptedAES, privateKey);
  const value = await decryptSecretValueWithAES(encryptedSecret, AESKey);

  return {
    name: encryptedSecret.name,
    value,
    description: encryptedSecret.description,
  };
};
